/**
 * AgentDashboard — Andy's management interface (auth-guarded).
 *
 * Panels:
 *   - Upcoming appointments (with the home address Andy visits + cancel)
 *   - Availability editor (weekly windows + blackout dates) — drives the
 *     public booking calendar
 *   - Quote requests
 *   - Leads (inbound GHL form submissions)
 *
 * All data comes from /api/admin/* (proxied auth; service_role server-side).
 */

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { labelFor } from '../lib/coverage';

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

// Friendly labels for the lead `source` slug shown in the Leads panel. Warm
// leads from the booking flow's step 1 come in as 'booking-started'.
const SOURCE_LABELS = {
  'booking-started': 'Booking started',
  'wp-quote-form': 'Website quote form',
  'wp-contact-form': 'Website contact form',
  'wp-unknown': 'Website',
};
const sourceLabel = (s) => SOURCE_LABELS[s] || s || '—';

// 6:00 AM .. 10:00 PM in 30-min steps, for the availability editor.
const TIME_OPTIONS = [];
for (let m = 360; m <= 1320; m += 30) TIME_OPTIONS.push(m);

function minuteLabel(m) {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  let hr = h % 12;
  if (hr === 0) hr = 12;
  return `${hr}:${String(mm).padStart(2, '0')} ${ampm}`;
}

function fmtEt(iso) {
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function rowsFromRules(rules) {
  const base = WEEKDAYS.map((_, wd) => ({
    weekday: wd,
    active: false,
    start_minute: 540,
    end_minute: 1020,
  }));
  for (const r of rules || []) {
    if (base[r.weekday]) {
      base[r.weekday] = {
        weekday: r.weekday,
        active: r.active !== false,
        start_minute: r.start_minute,
        end_minute: r.end_minute,
      };
    }
  }
  return base;
}

export default function AgentDashboard() {
  const { user, signOut } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [leads, setLeads] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [status, setStatus] = useState('loading');

  const [rows, setRows] = useState(rowsFromRules([]));
  const [blackouts, setBlackouts] = useState([]);
  const [availMsg, setAvailMsg] = useState('');
  const [savingAvail, setSavingAvail] = useState(false);
  const [newBlackoutDay, setNewBlackoutDay] = useState('');
  const [newBlackoutReason, setNewBlackoutReason] = useState('');

  const loadAppointments = async () => {
    const a = await api.get('/api/admin/appointments');
    setAppointments(a.data.appointments || []);
  };

  const loadAvailability = async () => {
    const av = await api.get('/api/admin/availability');
    setRows(rowsFromRules(av.data.rules));
    setBlackouts(av.data.blackouts || []);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [q, l, a, av] = await Promise.all([
          api.get('/api/admin/quotes'),
          api.get('/api/admin/leads'),
          api.get('/api/admin/appointments'),
          api.get('/api/admin/availability'),
        ]);
        if (!cancelled) {
          setQuotes(q.data.quotes || []);
          setLeads(l.data.leads || []);
          setAppointments(a.data.appointments || []);
          setRows(rowsFromRules(av.data.rules));
          setBlackouts(av.data.blackouts || []);
          setStatus('ready');
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const coverageText = (selections) =>
    (selections || []).map((s) => `${labelFor(s.type)} ${s.amount}`).join(', ');

  const addressText = (a) =>
    a.address_formatted ||
    [a.address_line1, a.address_line2, a.city, `${a.state} ${a.postal_code}`]
      .filter((p) => p && p.trim())
      .join(', ');

  const cancelAppointment = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Cancel this appointment? This frees the time slot.')) {
      return;
    }
    try {
      await api.patch(`/api/admin/appointments/${id}/cancel`);
      await loadAppointments();
    } catch {
      // eslint-disable-next-line no-alert
      window.alert('Could not cancel. Please try again.');
    }
  };

  const setRow = (wd, patch) =>
    setRows((rs) => rs.map((r) => (r.weekday === wd ? { ...r, ...patch } : r)));

  const saveAvailability = async () => {
    setAvailMsg('');
    for (const r of rows) {
      if (r.active && r.end_minute <= r.start_minute) {
        setAvailMsg(`${WEEKDAYS[r.weekday]}: end time must be after start time.`);
        return;
      }
    }
    setSavingAvail(true);
    try {
      await api.put('/api/admin/availability/rules', { rules: rows });
      await loadAvailability();
      setAvailMsg('Saved.');
    } catch {
      setAvailMsg('Could not save availability.');
    } finally {
      setSavingAvail(false);
    }
  };

  const addBlackout = async () => {
    if (!newBlackoutDay) return;
    try {
      const res = await api.post('/api/admin/availability/blackouts', {
        day: newBlackoutDay,
        reason: newBlackoutReason.trim() || undefined,
      });
      setBlackouts((b) =>
        [...b, res.data.blackout].sort((x, y) => x.day.localeCompare(y.day)),
      );
      setNewBlackoutDay('');
      setNewBlackoutReason('');
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert(err?.response?.data?.error || 'Could not add the date.');
    }
  };

  const removeBlackout = async (id) => {
    try {
      await api.delete(`/api/admin/availability/blackouts/${id}`);
      setBlackouts((b) => b.filter((x) => x.id !== id));
    } catch {
      // eslint-disable-next-line no-alert
      window.alert('Could not remove the date.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Agent Dashboard — iProtectYou.Family</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header style={headerStyle}>
        <div style={{ fontWeight: 700 }}>iProtectYou.Family — Agent</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '0.9rem', color: 'rgba(0,0,0,0.6)' }}>
            {user?.email}
          </span>
          <button onClick={signOut} style={signOutBtn}>
            Sign out
          </button>
        </div>
      </header>

      <main
        style={{
          maxWidth: 1040,
          margin: '0 auto',
          padding: 'clamp(0.85rem, 3vw, 1.5rem)',
          boxSizing: 'border-box',
        }}
      >
        {status === 'error' && (
          <p style={{ ...muted, color: '#9F1239' }}>
            Failed to load. Try refreshing.
          </p>
        )}

        {/* Upcoming appointments */}
        <section style={card}>
          <h2 style={h2}>Upcoming appointments ({appointments.length})</h2>
          {status === 'loading' && <p style={muted}>Loading…</p>}
          {status === 'ready' && appointments.length === 0 && (
            <p style={muted}>No upcoming appointments.</p>
          )}
          {appointments.length > 0 && (
            <div>
              {appointments.map((a) => (
                <div key={a.id} style={entry}>
                  <div style={entryTop}>
                    <span>
                      <span style={entryName}>{a.name}</span>{' '}
                      <span style={entryWhen}>{fmtEt(a.starts_at)}</span>
                    </span>
                    <button
                      style={cancelBtn}
                      onClick={() => cancelAppointment(a.id)}
                    >
                      Cancel
                    </button>
                  </div>
                  <div style={entrySub}>
                    {[
                      a.phone,
                      `Age: ${a.age == null ? '—' : a.age}`,
                      a.sex ? `Sex: ${a.sex === 'male' ? 'Male' : 'Female'}` : null,
                      `Smoker: ${a.smoker == null ? '—' : a.smoker ? 'Yes' : 'No'}`,
                      addressText(a),
                    ]
                      .filter(Boolean)
                      .join('  ·  ')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Quote requests */}
        <section style={card}>
          <h2 style={h2}>Quote requests ({quotes.length})</h2>
          {status === 'loading' && <p style={muted}>Loading…</p>}
          {status === 'ready' && quotes.length === 0 && (
            <p style={muted}>No quote requests yet.</p>
          )}
          {quotes.length > 0 && (
            <div>
              {quotes.map((q) => (
                <div key={q.id} style={entry}>
                  <div style={entryTop}>
                    <span style={entryName}>{q.name}</span>
                    <span style={entryStatus}>{q.status}</span>
                  </div>
                  <div style={entrySub}>
                    {[
                      q.phone,
                      q.email,
                      q.age != null ? `Age: ${q.age}` : null,
                      q.sex ? `Sex: ${q.sex === 'male' ? 'Male' : 'Female'}` : null,
                      `Smoker: ${q.smoker ? 'Yes' : 'No'}`,
                      coverageText(q.selections),
                    ]
                      .filter(Boolean)
                      .join('  ·  ')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Leads */}
        <section style={card}>
          <h2 style={h2}>Leads ({leads.length})</h2>
          {status === 'loading' && <p style={muted}>Loading…</p>}
          {status === 'ready' && leads.length === 0 && (
            <p style={muted}>
              No leads yet. Website enquiries and booking drop-offs land here.
            </p>
          )}
          {leads.length > 0 && (
            <div>
              {leads.map((l) => (
                <div key={l.id} style={entry}>
                  <div style={entryTop}>
                    <span style={entryName}>
                      {[l.first_name, l.last_name].filter(Boolean).join(' ') ||
                        '—'}
                    </span>
                    <span style={entryStatus}>{l.status}</span>
                  </div>
                  <div style={entrySub}>
                    {[l.email, l.phone, sourceLabel(l.source)]
                      .filter(Boolean)
                      .join('  ·  ')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Availability editor (kept at the bottom) */}
        <section style={card}>
          <h2 style={h2}>Availability</h2>
          <p style={{ ...muted, marginTop: 0 }}>
            Set your weekly hours. The booking page offers 1-hour slots, at least
            48 hours out.
          </p>
          <div style={tableWrap}>
            <table style={table}>
            <tbody>
              {rows.map((r) => (
                <tr key={r.weekday}>
                  <td style={{ ...td, width: 120 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={r.active}
                        onChange={(e) =>
                          setRow(r.weekday, { active: e.target.checked })
                        }
                      />
                      {WEEKDAYS[r.weekday]}
                    </label>
                  </td>
                  <td style={td}>
                    <select
                      value={r.start_minute}
                      disabled={!r.active}
                      onChange={(e) =>
                        setRow(r.weekday, {
                          start_minute: Number(e.target.value),
                        })
                      }
                      style={select}
                    >
                      {TIME_OPTIONS.map((m) => (
                        <option key={m} value={m}>
                          {minuteLabel(m)}
                        </option>
                      ))}
                    </select>{' '}
                    to{' '}
                    <select
                      value={r.end_minute}
                      disabled={!r.active}
                      onChange={(e) =>
                        setRow(r.weekday, { end_minute: Number(e.target.value) })
                      }
                      style={select}
                    >
                      {TIME_OPTIONS.map((m) => (
                        <option key={m} value={m}>
                          {minuteLabel(m)}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
            <button style={saveBtn} onClick={saveAvailability} disabled={savingAvail}>
              {savingAvail ? 'Saving…' : 'Save availability'}
            </button>
            {availMsg && <span style={muted}>{availMsg}</span>}
          </div>

          <h3 style={h3}>Blocked dates</h3>
          {blackouts.length === 0 && (
            <p style={muted}>No blocked dates. Add days off below.</p>
          )}
          {blackouts.length > 0 && (
            <ul style={{ margin: '0 0 0.75rem', paddingLeft: 18 }}>
              {blackouts.map((b) => (
                <li key={b.id} style={{ fontSize: '0.92rem', marginBottom: 4 }}>
                  {b.day}
                  {b.reason ? ` — ${b.reason}` : ''}{' '}
                  <button style={linkBtn} onClick={() => removeBlackout(b.id)}>
                    remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="date"
              value={newBlackoutDay}
              onChange={(e) => setNewBlackoutDay(e.target.value)}
              style={select}
            />
            <input
              type="text"
              placeholder="Reason (optional)"
              value={newBlackoutReason}
              onChange={(e) => setNewBlackoutReason(e.target.value)}
              style={{ ...select, minWidth: 180 }}
            />
            <button style={saveBtn} onClick={addBlackout} disabled={!newBlackoutDay}>
              Block date
            </button>
          </div>
        </section>
      </main>
    </>
  );
}

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '0.5rem',
  padding: '1rem clamp(0.85rem, 3vw, 1.5rem)',
  background: '#fff',
  borderBottom: '1px solid rgba(0,0,0,0.08)',
};

const signOutBtn = {
  padding: '0.5rem 0.9rem',
  background: 'transparent',
  border: '1px solid rgba(0,0,0,0.2)',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: '0.9rem',
};

const card = {
  background: '#fff',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 12,
  // Less side padding on narrow screens so tables have more room.
  padding: '1.25rem clamp(0.85rem, 3vw, 1.5rem)',
  marginBottom: '1.25rem',
};

const h2 = { margin: '0 0 0.75rem', fontSize: '1.1rem', color: 'var(--color-navy)' };
const h3 = { margin: '1.25rem 0 0.5rem', fontSize: '0.95rem', color: 'var(--color-navy)' };
const muted = { margin: 0, color: 'rgba(0,0,0,0.55)', fontSize: '0.95rem' };
const table = { width: '100%', borderCollapse: 'collapse' };
// Wrap each table so it scrolls horizontally WITHIN its card on narrow screens
// instead of pushing the whole page off the right edge.
const tableWrap = {
  width: '100%',
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
};
// Compact two-line entry rows (appointments / quotes / leads) — content stays
// stacked vertically instead of running wide off the right edge.
const entry = {
  padding: '0.55rem 0',
  borderBottom: '1px solid rgba(0,0,0,0.07)',
};
const entryTop = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};
const entryName = { fontWeight: 700, color: 'var(--color-navy)' };
const entryWhen = { color: 'rgba(0,0,0,0.6)', fontSize: '0.9rem' };
const entryStatus = {
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'rgba(0,0,0,0.5)',
};
const entrySub = {
  marginTop: 2,
  color: 'rgba(0,0,0,0.6)',
  fontSize: '0.88rem',
  lineHeight: 1.45,
};
const td = {
  padding: '0.5rem 0.6rem',
  borderBottom: '1px solid rgba(0,0,0,0.06)',
  fontSize: '0.92rem',
  whiteSpace: 'nowrap',
};
const select = {
  padding: '0.4rem 0.5rem',
  fontSize: '0.9rem',
  border: '1px solid rgba(0,0,0,0.15)',
  borderRadius: 6,
};
const saveBtn = {
  padding: '0.5rem 0.9rem',
  background: 'var(--color-navy)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 600,
};
const cancelBtn = {
  padding: '0.35rem 0.7rem',
  background: 'transparent',
  border: '1px solid #9F1239',
  color: '#9F1239',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: '0.85rem',
};
const linkBtn = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--color-royal)',
  textDecoration: 'underline',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: '0.85rem',
};
